/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { InView } from 'react-intersection-observer';
import Headline from '@ziynx/components/headlines';
import Link from '@ziynx/components/links';
import Card from '@ziynx/components/cards';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import {
  withAppManager, LoadingSpinner, formatDate, useNotification,
} from '@ziynx/components/utilities';
import Colors from '@ziynx/theme/colors';
import ActionTypes from '@ziynx/action-types';
import type { Viewports } from '@ziynx/flow-types/globals';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { StatementItemType, TalentType } from '@ziynx/flow-types/statements';
import { VIDEO_TRIGGER_TIME } from '../../constants';

type Props = $ReadOnly<{|
    viewport: Viewports,
    /**
     * Navigation stack
     */
    history: Object,
    |}>;

const StyledHeadline = styled(Headline)`
    margin-bottom: 40px;
`;

const LeftSlotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 688px;
`;

const RepliesWrapper = styled.div`
  ${({ isDesktop }) => isDesktop && 'margin-top: 40px;'}
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 40px;
`;

const LinkWrapper = styled.div`
  margin-top: 40px;
  margin-left: 40px;
`;

const BANNER_MESSAGE = {
  successAdd: 'has been added to your watchlist.',
  successRemove: 'has been removed from your watchlist.',
  error: 'An error occurred. Please try again later.',
  removing: 'Removing member.',
  adding: 'Adding member',
};

const ZIYNX_LOGO = 'https://ziynx-media-assets-dev.s3.amazonaws.com/ziynx_logo/ziynx-logo.png';

const VideoRepliesScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;
  const { contentId } = useParams();

  const statementItemService: StatementItemType = useSelector(
    ({ statementItem }) => statementItem[contentId] || {}, shallowEqual,
  );
  const updateWatchlistService = useSelector(
    ({ watchlist }) => ({
      ...watchlist[ActionTypes.watchlist.add.GROUP] || {},
      removeUser: watchlist[ActionTypes.watchlist.remove.GROUP] || {},
    }), shallowEqual,
  );
  const watchlistService = useSelector(
    ({ watchlist }) => watchlist?.GET_WATCHLIST_API ?? {}, shallowEqual,
  );

  const [talentName, setTalentName] = useState('');
  const [talentResults, setTalentResults] = useState([]);

  const dispatch = useDispatch();
  const { notificationDispatch } = useNotification();

  const resetNotification = (isRemove = false) => {
    if (isRemove) {
      dispatch({
        type: ActionTypes.watchlist.clearRemove,
      });
    } else {
      dispatch({
        type: ActionTypes.watchlist.clearAdd,
      });
    }
  };

  useEffect(() => {
    dispatch({
      type: ActionTypes.statementItem.getQuestionResponses.START,
      id: contentId,
      query: {
        page: 0,
      },
    });
    dispatch({
      type: ActionTypes.watchlist.get.START,
    });
  }, []);

  useEffect(() => {
    if (statementItemService?.success && !statementItemService?.paginatedSuccess) {
      setTalentResults(statementItemService?.results);
    } else if (!statementItemService?.paginating
      && !statementItemService?.fetching
      && statementItemService?.paginatedSuccess) {
      setTalentResults([...talentResults, ...statementItemService?.results]);

      /* This stops the redux persistor from using the most recent results when the user
      moves to a different page or refreshes */
      dispatch({
        type: ActionTypes.statementItem.clear,
        id: contentId,
      });
    }
  }, [statementItemService?.results]);

  /** REMOVING FROM WATCHLIST */
  useEffect(() => {
    if (updateWatchlistService?.removeUser?.success
      && !updateWatchlistService?.removeUser?.fetching
      && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: `${talentName} ${BANNER_MESSAGE?.successRemove}`,
        },
      });
      resetNotification(true);
    }
    if (updateWatchlistService?.removeUser?.error
      && !updateWatchlistService?.removeUser?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification(true);
    }
  }, [
    updateWatchlistService?.removeUser?.success,
    updateWatchlistService?.removeUser?.fetching,
    updateWatchlistService?.removeUser?.error,
    talentName,
  ]);

  /** ADDING TO WATCHLIST */
  useEffect(() => {
    if (updateWatchlistService?.success && !updateWatchlistService?.fetching && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: `${talentName} ${BANNER_MESSAGE?.successAdd}`,
          variant: 'success',
        },
      });
      resetNotification();
    }
    if (updateWatchlistService?.error && !updateWatchlistService?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification();
    }
  }, [updateWatchlistService?.success,
    updateWatchlistService?.fetching,
    updateWatchlistService?.error,
    talentName]);

  const leftSlot = (): React$Node => (
    <>
      <LeftSlotWrapper>
        <StyledHeadline
          variant="page"
          title={{ text: statementItemService?.results?.[0]?.question?.question }}
        />
      </LeftSlotWrapper>
    </>
  );

  const handleWatchlistAction = (talent: TalentType, fullName: string, isWatched: boolean) => {
    if (!isWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.watchlist.add.START,
        body: {
          userId: talent?.profile?._id,
        },
        userInfo: {
          _id: talent?.profile?._id,
          firstName: talent?.profile?.firstName,
          lastName: talent?.profile?.lastName,
        },
      });
    }
    if (isWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.watchlist.remove.START,
        body: {
          userId: talent?.profile?._id,
        },
        userInfo: {
          _id: talent?.profile?._id,
        },
      });
    }
  };

  const renderSection = useCallback((): Array<empty | $ReactElement<any>> => {
    const NEW_TALENT_RESULTS = (talentResults ?? []).map((talent: TalentType) => {
      const fullName = `${talent?.profile?.firstName ?? ''} ${talent?.profile?.lastName ?? ''}`;
      const videoSource = talent?.video?.sources?.mp4_1080_square?.url
        || talent?.video?.sources?.mp4?.url;

      const thumbnail = talent?.video?.sources?.thumbnail_square?.url
        || talent?.video?.sources?.thumbnail?.url;

      const thumbnailOptimized = talent?.video?.sources?.thumbnail_optimized?.url || '';

      const { watchedUserIds } = watchlistService;
      const isWatched: boolean = watchedUserIds?.includes(talent?.profile?._id);

      return {
        headline: {
          title: {
            text: fullName,
            variant: 'text',
            small: true,
            type: 'heading',
            onClick: () => history.push(`/discover/talent/${talent?.profile?._id}`),
          },
          squircleButton: {
            icon: {
              name: isWatched ? 'checkmark-fill' : 'plus',
            },
            inverted: isWatched,
            onClick: () => handleWatchlistAction(talent, fullName, isWatched),
          },
        },
        subtitle: {
          text: formatDate(talent?.createdAt),
        },
        thumbnail: {
          uri: thumbnail || ZIYNX_LOGO,
        },
        thumbnail_optimized: {
          uri: thumbnailOptimized,
        },
        video: {
          uri: videoSource,
          onTimeUpdate: (video) => {
            const { currentTime, duration } = video?.target || {};
            const timeCondition = (
              duration > VIDEO_TRIGGER_TIME) ? VIDEO_TRIGGER_TIME : (duration * 0.1);
            if ((currentTime >= timeCondition)
            && !statementItemService?.viewership?.[talent?._id]) {
              // Sending out notification that response was viewed
              dispatch({
                type: ActionTypes.trackingAction.viewedResponse.START,
                videoViewed: talent?._id,
                statementId: contentId,
              });
            }
          },
        },
      };
    });

    return (
      <Card
        viewport={viewport}
        flexColumn
        unlimitedCards
        variant="videoSquareGroup"
        data={NEW_TALENT_RESULTS}
      />
    );
  }, [talentResults]);

  const onPaginate = (inView): void => {
    if (statementItemService?.isLastPage || statementItemService?.fetching || !inView) return;
    dispatch({
      type: ActionTypes.statementItem.getQuestionResponses.START,
      id: contentId,
      query: {
        page: statementItemService?.page + 1,
      },
      paginating: true,
      fetching: false,
    });
  };

  const leftContent = (): React$Node => (
    <RepliesWrapper isDesktop={viewport === 'desktop'}>
      {statementItemService?.fetching ? (
        <SpinnerWrapper>
          <LoadingSpinner />
        </SpinnerWrapper>
      ) : renderSection()}
      <InView as="div" onChange={onPaginate}>
        {statementItemService?.paginating && (
          <SpinnerWrapper>
            <LoadingSpinner />
          </SpinnerWrapper>
        )}
      </InView>
    </RepliesWrapper>
  );

  return (
    <>
      <Helmet>
        <title>Video Replies | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={96} isVertical>
        <LinkWrapper>
          <Link
            variant="caret"
            color={Colors.GrayDark}
            onClick={() => history.push('/content')}
          >
            Back to Content
          </Link>
        </LinkWrapper>
        <PageTemplate
          viewport={viewport}
          leftSlot={leftSlot()}
          leftContent={leftContent()}
        />
      </ContentContainer>
    </>
  );
};

export default (withAppManager(VideoRepliesScreen): React$ComponentType<ManagedProps<Props>>);
